/**
 * The kanban's arithmetic, kept away from the DOM (`specs/M4-V2-kanban.md` §5, §6).
 *
 * A lane **is** a group-by bucket: B4 §8.8's grouped read already returns the pivot column's
 * whole vocabulary, in the column's own order, coloured, counted, paged, with `Blank` last.
 * Everything below is the small amount of logic on top — which bucket a virtual group id
 * names, what a drop writes, and which lane the pointer is over — and it is all pure.
 */
import { findColumnType } from '@elizmonday/shared';
import type { BoardColumn, BoardGroupView, ItemRow } from '../../board-view/data/types';

/** `gb:<columnId>:<bucketKey>` — the id `readGroupedBoard` gives a virtual group. */
export function parseLaneId(groupId: string): { columnId: string; bucketKey: string } | null {
  if (!groupId.startsWith('gb:')) return null;
  const rest = groupId.slice(3);
  const split = rest.indexOf(':');
  if (split === -1) return null;
  return { columnId: rest.slice(0, split), bucketKey: rest.slice(split + 1) };
}

/**
 * monday's sentinel for "no value", labelled `Blank`.
 *
 * Both sources now spell it this way: `services/board-read.ts` always did, and round 5x
 * reconciled `board-view/data/filtering.ts`, which used to answer `__blank__` / *Unassigned*
 * (defect 2 on `tasks/T-M4-V2-V4-V7.md`). One literal, so a drop on the blank lane clears the
 * cell whichever source the view is running on.
 */
export const BLANK_BUCKET_KEY = '$$$blank$$$';

export function isBlankBucket(bucketKey: string): boolean {
  return bucketKey === BLANK_BUCKET_KEY;
}

export interface Lane {
  readonly id: string;
  readonly bucketKey: string;
  readonly title: string;
  readonly color: string;
  readonly count: number;
  readonly collapsed: boolean;
}

export function lanesOf(groups: readonly BoardGroupView[], collapsedLaneKeys: readonly string[]): Lane[] {
  const collapsed = new Set(collapsedLaneKeys);
  return groups.map((group) => {
    const parsed = parseLaneId(group.id);
    const bucketKey = parsed?.bucketKey ?? group.id;
    return {
      id: group.id,
      bucketKey,
      title: group.title,
      color: group.color,
      count: group.page.total,
      collapsed: collapsed.has(bucketKey),
    };
  });
}

/**
 * The pivot columns the picker offers: a type that can bucket at all. A type that cannot turn
 * a bucket back into a value (`valueForBucket`) still lanes, read-only (§6.1).
 */
export function pivotCandidates(columns: readonly BoardColumn[]): BoardColumn[] {
  return columns.filter((column) => findColumnType(column.type)?.bucketsOf !== undefined);
}

/**
 * The lane axis a kanban opens on when its own `pivot_by_column` is empty (`[V2§4]`, P-025):
 * monday's first **status** column, then any other column that can bucket. A kanban created
 * through our own gallery used to take `candidates[0]`, which on a board whose first groupable
 * column is a `people` or a `creation_log` column is not a board anybody recognises.
 */
export function defaultPivotColumnId(columns: readonly BoardColumn[]): string | null {
  const candidates = pivotCandidates(columns);
  return candidates.find((column) => column.type === 'status')?.id ?? candidates[0]?.id ?? null;
}

export function canDragOnPivot(column: BoardColumn | undefined): boolean {
  if (column === undefined) return false;
  return findColumnType(column.type)?.valueForBucket !== undefined;
}

/**
 * What dropping `item` on `bucketKey` writes to the pivot column (§6.1). `null` clears the
 * cell, which is what the `Blank` lane means; `undefined` means "this type cannot be
 * dropped on" and the caller does nothing at all.
 */
export function laneDropValue(
  column: BoardColumn,
  item: ItemRow,
  bucketKey: string,
): unknown | undefined {
  const definition = findColumnType(column.type);
  if (definition === undefined) return undefined;
  if (isBlankBucket(bucketKey)) return null;
  if (definition.valueForBucket === undefined) return undefined;
  const settings = definition.settingsSchema.safeParse(column.settings);
  const current = item.values[column.id] ?? null;
  return definition.valueForBucket(bucketKey, settings.success ? settings.data : column.settings, current);
}

/** `true` when the item is already in that lane, so the drop is a no-op. */
export function alreadyInLane(column: BoardColumn, item: ItemRow, bucketKey: string): boolean {
  const definition = findColumnType(column.type);
  if (definition?.bucketsOf === undefined) return false;
  const value = item.values[column.id] ?? null;
  const buckets = definition.bucketsOf(value, column.settings, {});
  if (buckets.length === 0) return isBlankBucket(bucketKey);
  return buckets.some((bucket) => String(bucket.key) === bucketKey);
}

/** The MIME type the kanban drag puts on the dataTransfer, mirroring `text/ez-row`. */
export const KANBAN_DRAG_TYPE = 'text/ez-kanban-card';
